/**
 * Team option helpers (OS-566).
 *
 * A shipment's team travels exactly like its Client: the server stores the numeric IL_Team id but
 * reads it back as an `{ id, name }` object, and the screen sends the picked object back as-is
 * (the server takes the id out). These helpers cover the parts the Client field does not need:
 * the pickable list comes from a small `il_team` read instead of a server-side search, and the
 * bulk grid resolves a pasted team name or id against that list.
 */

/**
 * `il_team` read: `[{ id, name, status, assigned, il_trade_partner: [...] }, ...]` (or wrapped in
 * `{ il_team }`). `assigned` says whether the requesting user belongs to the team; a response
 * without the flag (older server) is treated as assigned so the picker never comes up empty.
 */
export function normalizeTeamOptions(data) {
    const list = Array.isArray(data) ? data : Array.isArray(data?.il_team) ? data.il_team : [];
    return list
        .filter((t) => t && typeof t === 'object' && t.id != null)
        .map((t) => ({
            id: Number(t.id),
            name: String(t.name ?? ''),
            status: t.status !== false,
            assigned: t.assigned !== false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Teams a user may pick for a shipment: only the user's own teams, like the Client dropdown only
 * offers the clients assigned to those teams, and never a dissolved team (status false).
 */
function activeTeamOptions(teams) {
    return (teams || []).filter((t) => t.status !== false && t.assigned !== false);
}

/** Coerce a cell/form value (number, numeric string, `{ id }` option) to a positive team id, else null. */
export function toTeamId(value) {
    const raw = value && typeof value === 'object' ? value.id : value;
    if (raw === '' || raw == null || typeof raw === 'boolean') return null;
    const num = Number(raw);
    return Number.isInteger(num) && num > 0 ? num : null;
}

/** The `{ id, name }` shape the server reads a team back as, and the screen sends it back in. */
export function toTeamOption(team) {
    return team ? { id: team.id, name: team.name } : null;
}

export function findTeamById(teams, value) {
    const id = toTeamId(value);
    if (id == null) return null;
    return (teams || []).find((t) => t.id === id) || null;
}

/**
 * Options for a picker that must still show the row's current team even when that team is not
 * pickable (dissolved, or one the user does not belong to such as TEMP) — otherwise the
 * autocomplete would render a blank for a valid value.
 */
export function pickableTeamOptions(teams, currentValue) {
    const active = activeTeamOptions(teams);
    const current = findTeamById(teams, currentValue);
    if (current && !active.some((t) => t.id === current.id)) return [...active, current];
    return active;
}

/**
 * Resolve a pasted/typed value to a team `{ id, name }` for the bulk grid.
 * - blank → '' (cell cleared, no warning)
 * - a team object / its JSON (copied from another team cell), a numeric id of a known team, or a
 *   team name (case-insensitive) → that team's `{ id, name }`
 * - anything else → undefined (caller warns and keeps the previous value)
 */
export function parseTeamValue(value, teams) {
    if (value === '' || value == null) return '';
    let raw = value;
    if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try {
            raw = JSON.parse(raw);
        } catch {
            return undefined;
        }
    }
    const byId = findTeamById(teams, raw);
    if (byId) return toTeamOption(byId);
    if (typeof raw === 'string') {
        const needle = raw.trim().toLowerCase();
        const byName = (teams || []).find((t) => t.name.trim().toLowerCase() === needle);
        if (byName) return toTeamOption(byName);
    }
    return undefined;
}
